// Thumbnail artwork, drawn as inline SVG so it stays crisp at any card size,
// costs a couple of KB, and needs no asset pipeline.
//
// Each entry returns an SVG string on a 16:10 viewBox (320×200).
//
// `skyline` is a deliberate stylization of the real game: Season 1's sky and
// hills, the dark checkered conveyor belt with its amber chevrons, the blocky
// visored runner, and a steel wall-run panel with its cyan speed stripe.
// `placeholder` is intentionally abstract — see the note in games.js.

const W = 320;
const H = 200;

function skyline() {
  // Perspective belt: near edge spans most of the frame, far edge converges
  // toward the horizon at y=96.
  const horizon = 96;
  const nearL = -40;
  const nearR = W + 40;
  const farL = 130;
  const farR = 190;

  // Chevrons marching down the belt. Width and drop both scale with the belt's
  // width at that depth, so every arrow keeps the same angle instead of
  // flattening into a dash as it recedes.
  const farHalf = (farR - farL) / 2;
  const nearHalf = (nearR - nearL) / 2;
  const farCx = (farL + farR) / 2;
  const nearCx = (nearL + nearR) / 2;

  let chevrons = '';
  for (let i = 0; i < 7; i++) {
    const t = (i + 0.45) / 7;                       // 0 = far, 1 = near
    const e = Math.pow(t, 1.9);                     // perspective easing
    const y = horizon + e * (H - horizon);
    const half = (farHalf + (nearHalf - farHalf) * e) * 0.58;
    const cx = farCx + (nearCx - farCx) * e;
    const drop = half * 0.44;                       // constant arrow angle
    const thick = 1.6 + e * 5.2;
    chevrons +=
      `<path d="M${(cx - half).toFixed(1)} ${y.toFixed(1)} ` +
      `L${cx.toFixed(1)} ${(y + drop).toFixed(1)} ` +
      `L${(cx + half).toFixed(1)} ${y.toFixed(1)}" ` +
      `fill="none" stroke="#f0c453" stroke-width="${thick.toFixed(1)}" ` +
      `stroke-linejoin="round" stroke-linecap="round" ` +
      `opacity="${(0.4 + t * 0.55).toFixed(2)}"/>`;
  }

  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img"
     aria-label="Skyline: a blocky runner on a dark conveyor belt receding toward green hills">
  <defs>
    <linearGradient id="sk-sky" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#b8dcf0"/><stop offset="0.62" stop-color="#dcecf4"/>
      <stop offset="1" stop-color="#eef4e8"/>
    </linearGradient>
    <linearGradient id="sk-belt" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#23262c"/><stop offset="1" stop-color="#33373f"/>
    </linearGradient>
    <clipPath id="sk-beltclip">
      <path d="M${farL} ${horizon} L${farR} ${horizon} L${nearR} ${H} L${nearL} ${H} Z"/>
    </clipPath>
  </defs>

  <rect width="${W}" height="${H}" fill="url(#sk-sky)"/>

  <!-- distant hills -->
  <path d="M0 ${horizon} Q54 74 108 ${horizon} T216 ${horizon} T${W} ${horizon} V${H} H0 Z" fill="#8fc08a"/>
  <path d="M0 ${horizon + 4} Q80 84 168 ${horizon + 4} T${W} ${horizon + 2} V${H} H0 Z" fill="#79b077"/>
  <!-- treeline -->
  <g fill="#4e8a58">
    <circle cx="34" cy="90" r="11"/><circle cx="47" cy="94" r="8"/>
    <circle cx="268" cy="88" r="12"/><circle cx="283" cy="93" r="9"/>
    <circle cx="212" cy="93" r="7"/>
  </g>
  <g fill="#3d7048">
    <path d="M96 96 l8-18 8 18 z"/><path d="M240 96 l7-15 7 15 z"/>
  </g>

  <!-- grass verge, then the belt -->
  <path d="M${farL} ${horizon} L${farR} ${horizon} L${nearR} ${H} L${nearL} ${H} Z" fill="url(#sk-belt)"/>
  <g clip-path="url(#sk-beltclip)">
    <!-- static checker so the block grid stays legible -->
    <g fill="#ffffff" opacity="0.05">
      <rect x="${nearL}" y="150" width="${W + 80}" height="26"/>
      <rect x="${nearL}" y="120" width="${W + 80}" height="14"/>
      <rect x="${nearL}" y="104" width="${W + 80}" height="8"/>
    </g>
    ${chevrons}
  </g>

  <!-- wall-run panel, right side -->
  <g>
    <path d="M244 ${horizon + 2} L${W} 118 L${W} ${H} L262 ${H} Z" fill="#3a4048"/>
    <path d="M244 ${horizon + 2} L${W} 118 L${W} 126 L245 ${horizon + 9} Z" fill="#59c8e8" opacity="0.9"/>
    <path d="M252 128 L${W} 146 L${W} 152 L253 134 Z" fill="#59c8e8" opacity="0.55"/>
  </g>

  <!-- the runner: blocky body, dark visor band -->
  <g transform="translate(140 108)">
    <ellipse cx="14" cy="76" rx="20" ry="5" fill="#000" opacity="0.22"/>
    <rect x="2" y="30" width="24" height="30" rx="7" fill="#e8edf2"/>
    <rect x="0" y="56" width="10" height="22" rx="4" fill="#dde4ea"/>
    <rect x="17" y="56" width="10" height="22" rx="4" fill="#eef3f7"/>
    <rect x="-1" y="74" width="12" height="6" rx="3" fill="#1a1d22"/>
    <rect x="16" y="74" width="12" height="6" rx="3" fill="#1a1d22"/>
    <circle cx="14" cy="18" r="15" fill="#eef3f7"/>
    <rect x="-1" y="13" width="30" height="5" rx="2.5" fill="#15181d"/>
  </g>
</svg>`;
}

// Sky Playground: a playground floating in the sky while a dragon circles and
// breaks it apart under the players. Palette taken from the game itself
// (sky #7fc7ff, accent #ffd34d) so the tile and the game read as one thing.
function skyplayground() {
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img"
     aria-label="Sky Playground: a dragon circling a playground platform floating in the clouds">
  <defs>
    <linearGradient id="sp-sky" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#5cb3f5"/><stop offset="0.55" stop-color="#7fc7ff"/>
      <stop offset="1" stop-color="#b9e2ff"/>
    </linearGradient>
    <linearGradient id="sp-deck" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#9fd66b"/><stop offset="1" stop-color="#5e9c46"/>
    </linearGradient>
  </defs>

  <rect width="${W}" height="${H}" fill="url(#sp-sky)"/>

  <!-- clouds -->
  <g fill="#ffffff" opacity="0.85">
    <ellipse cx="52" cy="46" rx="26" ry="11"/><ellipse cx="72" cy="41" rx="18" ry="13"/>
    <ellipse cx="258" cy="150" rx="30" ry="12"/><ellipse cx="236" cy="146" rx="19" ry="10"/>
    <ellipse cx="150" cy="26" rx="20" ry="8"/>
  </g>

  <!-- the floating playground: deck, underside, a slide and a frame -->
  <g>
    <path d="M78 128 L214 128 L196 150 L96 150 Z" fill="#6b5138"/>
    <ellipse cx="146" cy="128" rx="68" ry="17" fill="url(#sp-deck)"/>
    <!-- crumbling edge: chunks already knocked loose and falling -->
    <g fill="#6b5138">
      <rect x="206" y="140" width="9" height="7" rx="2" transform="rotate(18 210 143)"/>
      <rect x="222" y="158" width="7" height="6" rx="2" transform="rotate(-24 225 161)"/>
      <rect x="196" y="172" width="6" height="5" rx="2" transform="rotate(35 199 174)"/>
    </g>
    <!-- climbing frame -->
    <g stroke="#ffd34d" stroke-width="3" stroke-linecap="round" fill="none">
      <path d="M104 126 v-24 h30 v24"/><path d="M104 112 h30"/>
    </g>
    <!-- slide -->
    <path d="M170 104 L188 130" stroke="#ff8a5c" stroke-width="5" stroke-linecap="round" fill="none"/>
    <circle cx="170" cy="102" r="4" fill="#ff8a5c"/>
    <!-- two tiny players, one already tipping off the edge -->
    <g fill="#f4f8fb">
      <circle cx="132" cy="116" r="4.2"/><rect x="129.6" y="120" width="5" height="8" rx="2"/>
      <g transform="rotate(38 200 140)">
        <circle cx="200" cy="136" r="4"/><rect x="197.8" y="140" width="4.6" height="7.5" rx="2"/>
      </g>
    </g>
  </g>

  <!-- the dragon, banking in from the left above the deck -->
  <g fill="#3f2f5e">
    <!-- far wing -->
    <path d="M96 74 L58 40 L74 76 L46 70 L82 92 Z" opacity="0.55"/>
    <!-- body + tail -->
    <path d="M92 84 Q126 72 156 78 Q182 83 206 70 Q188 90 158 90 Q124 92 100 96 Z"/>
    <path d="M206 70 Q220 66 232 54 Q222 74 208 78 Z"/>
    <!-- head -->
    <path d="M86 82 Q72 78 64 86 Q74 92 88 92 Z"/>
    <!-- near wing, raised -->
    <path d="M120 82 L104 44 L142 72 L170 42 L152 84 Z"/>
  </g>
  <circle cx="72" cy="84" r="1.9" fill="#ffd34d"/>

  <!-- a breath of fire licking the deck edge -->
  <path d="M64 90 Q84 104 96 122" stroke="#ff9a3c" stroke-width="4" stroke-linecap="round"
        fill="none" opacity="0.75"/>
  <path d="M70 92 Q86 104 94 118" stroke="#ffd34d" stroke-width="2" stroke-linecap="round"
        fill="none" opacity="0.9"/>
</svg>`;
}

// DragonScape: a real frame of the game, not a drawing of it. Captured from
// the island itself — same voxels, same character GLB and clips, same sheep,
// same lighting — by the dev-only `dragonscape.html?poster` route, with the
// painted logo laid over the top. Re-shoot it with tools/shoot-poster.mjs.
function dragonscape() {
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img"
     aria-label="DragonScape: gentlemen and their flock in a blocky forest, one swinging in on a grapple line">
  <image href="./dragonscape-shot.jpg" x="0" y="0" width="${W}" height="${H}"
         preserveAspectRatio="xMidYMid slice"/>
</svg>`;
}

// RaceMadness: a captured frame of the game itself — the player car mid-flip
// through the gold repair ring off Snap Court's mega ramp — shot by the
// dev-only `racemadness.html?poster` route via tools/shoot-poster.mjs.
//
// RE-SHOOT IT WHENEVER THE ROSTER CHANGES. This is the only picture of the game
// anyone sees before they launch it, so it has to show cars that are actually
// in it: the first version was photographed with the ten voxel block cars still
// on the grid and outlived them by a whole release.
//
//   npm run dev
//   node tools/shoot-poster.mjs --url http://localhost:5173/racemadness.html \
//     --out public/racemadness-shot.jpg
//
// And re-read the aria-label below while you are here — it describes the JPEG,
// so a re-shoot that changes what is in frame changes it too.
function racemadness() {
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img"
     aria-label="RaceMadness: a red race car flipping through a gold ring over a stunt ramp">
  <image href="./racemadness-shot.jpg" x="0" y="0" width="${W}" height="${H}"
         preserveAspectRatio="xMidYMid slice"/>
</svg>`;
}

function placeholder() {
  // Abstract lattice + lock. Reads as "slot reserved", never as a screenshot.
  let grid = '';
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < 4; j++) {
      const on = (i * 3 + j * 5) % 4 === 0;
      grid +=
        `<rect x="${28 + i * 46}" y="${24 + j * 40}" width="34" height="28" rx="5" ` +
        `fill="currentColor" opacity="${on ? 0.16 : 0.06}"/>`;
    }
  }
  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" role="img"
     aria-label="Artwork not available — game in development">
  <rect width="${W}" height="${H}" fill="#12161d"/>
  <g color="currentColor">${grid}</g>
  <g transform="translate(${W / 2} ${H / 2})" fill="none" stroke="currentColor"
     stroke-width="2.4" opacity="0.55" stroke-linecap="round">
    <rect x="-13" y="-4" width="26" height="20" rx="4"/>
    <path d="M-7 -4 v-7 a7 7 0 0 1 14 0 v7"/>
  </g>
</svg>`;
}

export const ART = { skyline, dragonscape, racemadness, skyplayground, placeholder };

export function artFor(key) {
  return (ART[key] ?? ART.placeholder)();
}
